import React, { useState, useRef } from 'react';
import { useTranslation } from '../hooks/useTranslation';

interface NewsImageUploadProps {
  currentImageUrl?: string;
  onImageChange: (imageUrl: string) => void;
  onImageUpload?: (file: File) => Promise<string>; // Returns new image URL
  newsSlug?: string;
  disabled?: boolean;
}

const NewsImageUpload: React.FC<NewsImageUploadProps> = ({
  currentImageUrl,
  onImageChange,
  onImageUpload,
  newsSlug,
  disabled = false
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string>('');
  const [dragOver, setDragOver] = useState(false);

  // File validation
  const validateFile = (file: File): { valid: boolean; error?: string } => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: t('admin.error.invalid_file_type')
      };
    }

    if (file.size > maxSize) {
      return {
        valid: false,
        error: t('admin.error.file_too_large')
      };
    }

    return { valid: true };
  };

  // Handle file selection
  const handleFileSelect = (file: File) => {
    const validation = validateFile(file);
    if (!validation.valid) {
      setUploadError(validation.error || 'Invalid file');
      return;
    }

    setSelectedFile(file);
    setUploadError('');

    // Create preview URL
    const preview = URL.createObjectURL(file);
    setPreviewUrl(preview);
  };

  // Handle file input change
  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Handle drag and drop
  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Upload selected file
  const handleUpload = async () => {
    if (!selectedFile || !onImageUpload) return;

    setUploading(true);
    setUploadError('');

    try {
      const newImageUrl = await onImageUpload(selectedFile);
      onImageChange(newImageUrl);
      
      // Clean up preview
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl('');
      setSelectedFile(null);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (error: any) {
      console.error('Upload error:', error);
      setUploadError(error.message || t('admin.error.upload_failed'));
    } finally {
      setUploading(false);
    }
  };

  // Cancel selection
  const handleCancel = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl('');
    setSelectedFile(null);
    setUploadError('');
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Open file picker
  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  // Get display image URL
  const getDisplayImageUrl = () => {
    if (previewUrl) return previewUrl;
    if (currentImageUrl) return currentImageUrl;
    return '';
  };

  const displayImageUrl = getDisplayImageUrl();

  return (
    <div className="space-y-4">
      {/* Current/Preview Image */}
      {displayImageUrl && (
        <div className="space-y-2">
          <label className="block text-gray-300 text-sm font-medium">
            {previewUrl ? 'Preview' : t('admin.form.current_image')}
          </label>
          <div className="relative">
            <img
              src={displayImageUrl}
              alt="News"
              className="w-full max-w-md h-48 object-cover rounded-lg border border-white/10"
            />
            {previewUrl && (
              <div className="absolute top-2 right-2">
                <span className="bg-blue-500 text-white px-2 py-1 rounded text-xs">
                  Preview
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload Area */}
      <div className="space-y-3">
        <label className="block text-gray-300 text-sm font-medium">
          {currentImageUrl ? t('admin.form.change_image') : t('admin.form.upload_image')}
        </label>

        {/* Drag & Drop Area */}
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
            dragOver
              ? 'border-purple-500 bg-purple-500/10'
              : 'border-white/20 hover:border-white/40'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={!disabled ? openFilePicker : undefined}
        >
          <div className="space-y-2">
            <div className="text-4xl">📷</div>
            <div className="text-gray-300">
              <p className="font-medium">
                {selectedFile ? selectedFile.name : t('admin.form.choose_image')}
              </p>
              <p className="text-sm text-gray-400">
                Drag & drop or click to select
              </p>
              <p className="text-xs text-gray-500">
                JPG, PNG, GIF, WebP up to 5MB
              </p>
            </div>
          </div>
        </div>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled}
        />

        {/* Error Display */}
        {uploadError && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3">
            <p className="text-red-300 text-sm">{uploadError}</p>
          </div>
        )}

        {/* Action Buttons */}
        {selectedFile && (
          <div className="flex space-x-3">
            {onImageUpload && (
              <button
                onClick={handleUpload}
                disabled={uploading || disabled}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? (
                  <span className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>{t('admin.form.upload_progress')}</span>
                  </span>
                ) : (
                  t('admin.form.upload_image')
                )}
              </button>
            )}
            
            <button
              onClick={handleCancel}
              disabled={uploading || disabled}
              className="px-4 py-2 bg-white/10 text-gray-300 rounded-lg hover:bg-white/20 hover:text-white transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('admin.button.cancel')}
            </button>
          </div>
        )}

        {/* Upload Progress */}
        {uploading && (
          <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-3">
            <p className="text-blue-300 text-sm">
              {t('admin.form.upload_progress')} {selectedFile?.name}...
            </p>
          </div>
        )}
      </div>

      {/* File Info */}
      {selectedFile && !uploading && (
        <div className="bg-white/5 rounded-lg p-3">
          <div className="text-sm text-gray-400 space-y-1">
            <div>Name: {selectedFile.name}</div>
            <div>Size: {Math.round(selectedFile.size / 1024)} KB</div>
            <div>Type: {selectedFile.type}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsImageUpload; 